import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useRef } from "react";

import { TransformationStoryAttribution } from "~/components/transformation-story-attribution";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { getCDNImageUrl } from "~/components/ui/cdn-image";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/cn";
import { getTransformationStoriesFn } from "~/lib/functions/transformation-story";
import type { PublicTransformationStory } from "~/lib/shared/transformation-story";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const cardAccents = [
  "from-amber-50/80 to-orange-50/60",
  "from-purple-50/60 to-pink-50/40",
  "from-teal-50/60 to-emerald-50/40",
  "from-blue-50/60 to-indigo-50/40",
  "from-rose-50/60 to-pink-50/40",
  "from-violet-50/60 to-purple-50/40",
];

const quoteColors = [
  "text-amber-200",
  "text-purple-200",
  "text-teal-200",
  "text-blue-200",
  "text-rose-200",
  "text-violet-200",
];

interface CarouselStoryCardProps {
  story: PublicTransformationStory;
  index: number;
}

function CarouselStoryCard({ story, index }: CarouselStoryCardProps) {
  const accentIndex = index % cardAccents.length;
  const cardAccent = cardAccents[accentIndex];
  const quoteColor = quoteColors[accentIndex];

  return (
    <article
      className={cn(
        "group relative w-80 overflow-hidden rounded-2xl",
        "bg-gradient-to-br",
        cardAccent,
        "border border-white/60 shadow-sm",
        "transition-shadow duration-300 ease-out",
        "hover:shadow-lg hover:shadow-gray-200/50",
      )}
    >
      <Quote
        className={cn(
          "absolute -top-2 -right-2 h-16 w-16 rotate-12",
          quoteColor,
          "opacity-60",
        )}
        strokeWidth={1}
      />

      <div className="relative p-5">
        <blockquote className="mb-4">
          <p className="line-clamp-4 font-serif text-sm leading-relaxed text-gray-700">
            "{story.body}"
          </p>
        </blockquote>

        <footer className="flex items-center gap-3 border-t border-gray-200/50 pt-3">
          <Avatar className="h-9 w-9 ring-2 ring-white/80">
            {story.avatarUrl ? (
              <AvatarImage src={getCDNImageUrl(story.avatarUrl, 72)} alt="" />
            ) : null}
            <AvatarFallback className="bg-gradient-to-br from-purple-100 to-amber-100 text-xs font-medium text-gray-600">
              {getInitials(story.name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <cite className="block truncate text-sm font-medium text-gray-900 not-italic">
              {story.name}
            </cite>
            <TransformationStoryAttribution story={story} />
          </div>
        </footer>
      </div>
    </article>
  );
}

function CarouselStoryCardSkeleton() {
  return (
    <div className="w-80 animate-pulse rounded-2xl bg-gradient-to-br from-gray-100/80 to-gray-50/60 p-5">
      <div className="mb-4 space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-4/6" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      <div className="flex items-center gap-3 border-t border-gray-200/30 pt-3">
        <Skeleton className="h-9 w-9 rounded-full" />
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-2 w-16" />
        </div>
      </div>
    </div>
  );
}

/**
 * Self-contained horizontal carousel of transformation stories.
 * Fetches its own data and displays stories in a horizontally scrolling row.
 * Uses modern CSS scroll-snap and scroll-driven animations.
 *
 * @alias StoriesRow, StoryCarousel, TransformationCarousel
 */
export function StoriesCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["transformation-stories", "carousel"],
    queryFn: () =>
      getTransformationStoriesFn({
        data: { page: 1, limit: 12, seed: 42 },
      }),
    staleTime: 5 * 60 * 1000,
  });

  const stories = data?.data ?? [];

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  if (!isLoading && stories.length === 0) {
    return null;
  }

  return (
    <section className="py-16">
      <div className="mx-auto max-w-screen-xl px-4">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-1 text-sm font-medium tracking-wide text-purple-700 uppercase">
                Transformation Stories
              </p>
              <h2 className="text-text-primary font-heading text-3xl font-medium tracking-tight md:text-4xl">
                Real results from real people
              </h2>
            </div>
            <p className="text-text-secondary max-w-2xl text-lg leading-relaxed">
              See how coaching has transformed lives. These stories showcase the
              powerful impact of working with the right coach.
            </p>
          </div>
          <Link to="/stories" className="flex-shrink-0">
            <Button size="lg">View all stories</Button>
          </Link>
        </div>

        {/* Carousel with CSS scroll-snap */}
        <div className="group/carousel relative">
          {/* Scroll buttons - positioned outside content flow, high z-index */}
          <button
            onClick={() => scroll("left")}
            className={cn(
              "absolute top-1/2 -left-4 z-20 -translate-y-1/2",
              "h-10 w-10 rounded-full border border-gray-200 bg-white/95 shadow-lg",
              "flex items-center justify-center",
              "transition-opacity duration-200",
              "hover:bg-white",
              "opacity-0 group-hover/carousel:opacity-100",
              "focus:opacity-100 focus:ring-2 focus:ring-brand-500 focus:outline-none",
            )}
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>

          <button
            onClick={() => scroll("right")}
            className={cn(
              "absolute top-1/2 -right-4 z-20 -translate-y-1/2",
              "h-10 w-10 rounded-full border border-gray-200 bg-white/95 shadow-lg",
              "flex items-center justify-center",
              "transition-opacity duration-200",
              "hover:bg-white",
              "opacity-0 group-hover/carousel:opacity-100",
              "focus:opacity-100 focus:ring-2 focus:ring-brand-500 focus:outline-none",
            )}
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>

          {/* Scrollable container with mask for fade effect */}
          <div
            ref={scrollRef}
            className={cn(
              "flex gap-5 overflow-x-auto",
              "snap-x snap-mandatory scroll-smooth",
              "py-2",
              // CSS mask for fade edges - uses transparent so it works on any background
              "[mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)]",
              // Hide scrollbar
              "scrollbar-hide",
            )}
            style={{
              // Ensure content can scroll to edges
              scrollPaddingInline: "1rem",
            }}
          >
            {/* Spacer for left edge */}
            <div className="w-4 shrink-0" aria-hidden="true" />

            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="shrink-0 snap-start">
                    <CarouselStoryCardSkeleton />
                  </div>
                ))
              : stories.map((story, index) => (
                  <div key={story.id} className="shrink-0 snap-start">
                    <CarouselStoryCard story={story} index={index} />
                  </div>
                ))}

            {/* Spacer for right edge */}
            <div className="w-4 shrink-0" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
}
